// src/service/KeycloakService.js
import Keycloak from 'keycloak-js';
import axios from 'axios';
import store from '@/store';
import { isEmpty } from '@/main';

let keycloak = null;
let _axiosInterceptorConfigured = false;
const PENDING_AUTH_ROUTE_STORAGE_KEY = 'selectio.pending-auth-route';

const buildKeycloakConfig = () => {
  const runtimeKeycloak =
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.keycloak)
      ? window.__APP_CONFIG__.keycloak
      : null;

  return {
    url: runtimeKeycloak?.url || '',
    realm: runtimeKeycloak?.realm || 'selectio',
    clientId: runtimeKeycloak?.clientId || 'selectio-v2',
  };
};

export const getKeycloak = () => {
  if (!keycloak) {
    const keycloakConfig = buildKeycloakConfig();
    keycloak = new Keycloak(keycloakConfig);
  }
  return keycloak;
};

let _onAuthenticatedCallback = null;
let _onTokenUpdatedCallback = null;


const getSilentCheckSsoRedirectUri = () => {
  if (process.env.NODE_ENV === 'development') return undefined; // Disabilita in sviluppo per evitare errori CSP e semplificare il setup locale

  const currentPath = window.location.pathname || '/';
  const appBasePath = currentPath.endsWith('/')
    ? currentPath
    : currentPath.substring(0, currentPath.lastIndexOf('/') + 1);
  return `${window.location.origin}${appBasePath}sso-silent-check.html`;
};

const getCurrentRedirectUri = () => window.location.href;

const getCurrentHashRoute = () => {
  const hash = window.location.hash || '';
  if (!hash.startsWith('#/')) {
    return '';
  }
  const route = hash.substring(1);
  if (!route || route === '/' || route === '/landing-page') {
    return '';
  }
  return route;
};

const persistPendingRouteFromHash = () => {
  const route = getCurrentHashRoute();
  if (!route) {
    return;
  }
  sessionStorage.setItem(PENDING_AUTH_ROUTE_STORAGE_KEY, route);
};

const loginWithCurrentRedirect = (kc) => {
  persistPendingRouteFromHash();
  kc.login({ redirectUri: getCurrentRedirectUri() });
};

const buildLogoutEndpoint = (kc) => {
  const authServerUrl = (kc?.authServerUrl || '').trim().replace(/\/$/, '');
  const realm = (kc?.realm || '').trim();
  if (!authServerUrl || !realm) {
    return '';
  }
  return `${authServerUrl}/realms/${encodeURIComponent(realm)}/protocol/openid-connect/logout`;
};

const appendHiddenInput = (form, name, value) => {
  if (value == null || value === '') {
    return;
  }
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = String(value);
  form.appendChild(input);
};

const submitLogoutPost = (kc, redirectUri) => {
  if (typeof document === 'undefined' || !document.body) {
    return false;
  }
  const endpoint = buildLogoutEndpoint(kc);
  if (!endpoint) {
    return false;
  }

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = endpoint;
  form.style.display = 'none';

  // Keep logout parameters in request body to avoid oversized query strings blocked by WAF/proxies.
  appendHiddenInput(form, 'client_id', kc?.clientId);
  appendHiddenInput(form, 'post_logout_redirect_uri', redirectUri);
  appendHiddenInput(form, 'id_token_hint', kc?.idToken);

  document.body.appendChild(form);
  form.submit();
  return true;
};

/**
 * Inizializza Keycloak.
 * @param {Function} onAuthenticatedCallback - Funzione da chiamare dopo il login avvenuto con successo.
 */
export const initKeycloak = (onAuthenticatedCallback) => {
  _onAuthenticatedCallback = onAuthenticatedCallback;

  const kc = getKeycloak();

  // Configurazione base
  const initOptions = {
    onLoad: 'check-sso',
    checkLoginIframe: false,
    pkceMethod: 'S256',
  };

  // Aggiungi l'URI solo se getSilentCheckSsoRedirectUri restituisce un valore valido (es. in produzione)
  const silentUri = getSilentCheckSsoRedirectUri();
  if (silentUri) {
    initOptions.silentCheckSsoRedirectUri = silentUri;
  }

  // Passiamo le opzioni dinamiche a kc.init
  kc.init(initOptions).then((authenticated) => {
    const tokenParsed = kc.tokenParsed;

    if (isEmpty(tokenParsed.groups)) {
      console.error("USER NOT FOUND IN IAM")
      logout();
    }

    if (authenticated) {
      localStorage.removeItem('vuex');
      store.commit('RESET_STATE_INDEX');
      store.commit('acl/RESET_STATE_ACL');
      store.commit('buyers/RESET_STATE_BUYERS');
      setupAxiosInterceptor();
      kc.onTokenExpired = () => {
        kc.updateToken(70).then(() => {
          if (_onTokenUpdatedCallback) _onTokenUpdatedCallback(kc.token);
        }).catch(() => {
          loginWithCurrentRedirect(kc);
        });
      };
      if (_onAuthenticatedCallback) _onAuthenticatedCallback();
    } else {
      console.warn('Utente non autenticato. Redirect al login Keycloak...');
      loginWithCurrentRedirect(kc);
    }
  }).catch((err) => {
    console.error('Errore inizializzazione Keycloak:', err);
    forceLogout();
  });
};

/**
 * Configura Axios per iniettare il token automaticamente.
 */
const setupAxiosInterceptor = () => {
  if (_axiosInterceptorConfigured) {
    return;
  }
  _axiosInterceptorConfigured = true;

  axios.interceptors.request.use(async (config) => {
    const kc = getKeycloak();
    if (kc.token) {
      try {
        // Aggiorna il token se scade entro 70 secondi
        await kc.updateToken(70);
        config.headers.Authorization = `Bearer ${kc.token}`;
        if (_onTokenUpdatedCallback) _onTokenUpdatedCallback(kc.token);
      } catch (error) {
        console.error('Errore refresh token', error);
        loginWithCurrentRedirect(kc);
      }
    }
    return config;
  }, (error) => {
    return Promise.reject(error);
  });
};

export const forceLogout = async () => {
  const kc = getKeycloak();

  const appUrl = `${window.location.origin}/Selectio/#/`;
  const loginUrl = await kc.createLoginUrl({
    redirectUri: appUrl,
    prompt: "login"
  });
  window.location.href = loginUrl;
};

export const logout = () => {
  const baseUrl = `${window.location.origin}${window.location.pathname}`;
  const kc = getKeycloak();
  const postSubmitted = submitLogoutPost(kc, baseUrl);
  if (!postSubmitted) {
    kc.logout({ redirectUri: baseUrl });
  }
};

export const getToken = () => {
  const kc = getKeycloak();
  return kc.token;
};

export const isAuthenticated = () => {
  const kc = getKeycloak();
  return !!kc.token;
};

export const onTokenUpdated = (callback) => {
  _onTokenUpdatedCallback = callback;
};

// Esportiamo il getter per usi avanzati
export default getKeycloak;
